import React, { ReactElement, useCallback, useMemo, ChangeEventHandler } from 'react';
import Button from '../Button/Button';
import { CAD_RADS } from '../../config';
import { StatsDataType } from '../../context/types';

interface Props {
  stats: StatsDataType;
  editingReport: boolean;
  handleUpdateReportStats: (stat: string, newValue: string) => void;
  // If true, user will be given a free text field when editing instead of only being able to pick modifiers
  editable?: boolean;
}

interface CadRadModifiers {
  N: boolean;
  S: boolean;
  G: boolean;
  V: boolean;
}

function createModifiers(modifiers: CadRadModifiers) {
  const N = modifiers.N ? ['N'] : [];
  const S = modifiers.S ? ['S'] : [];
  const G = modifiers.G ? ['G'] : [];
  const V = modifiers.V ? ['V'] : [];

  const allModifiers = [...N, ...S, ...G, ...V];

  if (allModifiers.length === 0) {
    return '';
  }

  return allModifiers.reduce((a, b) => a + '/' + b);
}

export function CadRadsStat({
  stats,
  editingReport,
  handleUpdateReportStats,
  editable = false,
}: Props): ReactElement<Props> {
  const [cadrad, ...modifiers]: (string | undefined)[] = useMemo(() => `${stats.value}`.replace(' ', '/').split('/'), [
    stats.value,
  ]);

  const updateValue: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      handleUpdateReportStats('cad_rads', event.target.value);
    },
    [handleUpdateReportStats]
  );

  const modifierStates: CadRadModifiers = useMemo(() => {
    return {
      N: modifiers.includes('N'),
      S: modifiers.includes('S'),
      G: modifiers.includes('G'),
      V: modifiers.includes('V'),
    };
  }, [modifiers]);

  const updateModifiers = useCallback(
    (value: 'N' | 'S' | 'G' | 'V') => {
      const newState: CadRadModifiers = {
        ...modifierStates,
        [value]: !modifierStates[value],
      };

      handleUpdateReportStats('cad_rads', `${cadrad}/${createModifiers(newState)}`);
    },
    [cadrad, modifierStates, handleUpdateReportStats]
  );

  const renderToggleables = useCallback(
    (mod: 'N' | 'S' | 'G' | 'V') => {
      return (
        <Button
          onClick={() => updateModifiers(mod)}
          selected={modifierStates[mod]}
          theme="tertiary-square"
          title={CAD_RADS[mod]}
        >
          {mod}
        </Button>
      );
    },
    [modifierStates, updateModifiers]
  );

  return (
    <div className="value-list__item">
      <dt className="value-list__label">{stats.label}</dt>
      <dd className="value-list__value">
        {editingReport && !editable && (
          <>
            {cadrad.length > 0 ? cadrad : '-'}
            <div className="cadrads-modifier">
              {Object.keys(modifierStates)
                .filter((_x): _x is 'N' | 'S' | 'G' | 'V' => true)
                .map((x: 'N' | 'S' | 'G' | 'V', index: number) => (
                  <div className="cadrads-modifier__cell" key={`${x}-${index}`}>
                    {renderToggleables(x)}
                  </div>
                ))}
            </div>
          </>
        )}
        {editingReport && editable && <input type="text" value={stats.value} onChange={updateValue} />}
        {!editingReport && (stats.value ? stats.value : '-')}
      </dd>
    </div>
  );
}
